import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, integer, numeric, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { organization } from '../../auth'
import { RoyaltyToBooks } from '../../royalty'
import { BookToBookTheme } from './book-to-book-theme'
import { BookToContributor } from './book-to-contributor'
import { BookToKeyword } from './book-to-keyword'
import { BookFormat, BookLevel } from './category'

export const Book = pgTable(
  'book',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    slug: text().notNull(),
    synopsis: text().notNull(),
    isbn: text().unique(),
    bookPublishedOn: timestamp({ mode: 'date', withTimezone: true, precision: 3 }),
    publishedAt: timestamp('published_at', { mode: 'date', withTimezone: true, precision: 3 }),
    minAge: numeric('min_age', { mode: 'number' }).notNull(),
    maxAge: numeric('max_age', { mode: 'number' }).notNull(),
    bookLevelId: uuid()
      .notNull()
      .references(() => BookLevel.id, { onDelete: 'restrict' }),
    bookFormatId: uuid()
      .notNull()
      .references(() => BookFormat.id, { onDelete: 'restrict' }),
    coverKey: text().notNull(),
    contentKey: text().notNull(),
    width: numeric('width', { mode: 'number' }),
    height: numeric('height', { mode: 'number' }),
    page: numeric('page', { mode: 'number' }),
    isFree: boolean().notNull(),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    isActive: boolean().notNull().default(true),
    likesCount: integer().notNull().default(0),
    viewsCount: integer().notNull().default(0),
    legacyBookId: integer(),
    legacyNeonBookId: uuid(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
  },
  (table) => [
    index('book_slug_idx').on(table.slug),
    index('book_isbn_idx').on(table.isbn),
    index('book_book_published_on_idx').on(table.bookPublishedOn),
    index('book_min_age_idx').on(table.minAge),
    index('book_max_age_idx').on(table.maxAge),
    index('book_level_idx').on(table.bookLevelId),
    index('book_format_idx').on(table.bookFormatId),
    index('book_organization_idx').on(table.organizationId),
    index('book_is_free_idx').on(table.isFree),
    index('book_published_at_idx').on(table.publishedAt),
    index('book_updated_at_idx').on(table.updatedAt),
    index('book_created_at_idx').on(table.createdAt),
    index('book_is_active_idx').on(table.isActive),
    index('book_legacy_book_id_idx').on(table.legacyBookId),
    index('book_legacy_neon_book_id_idx').on(table.legacyNeonBookId),
    index('book_likes_count_idx').on(table.likesCount),
    index('book_views_count_idx').on(table.viewsCount),
  ],
)

export const bookRelations = relations(Book, ({ one, many }) => ({
  bookLevel: one(BookLevel, {
    fields: [Book.bookLevelId],
    references: [BookLevel.id],
  }),
  bookFormat: one(BookFormat, {
    fields: [Book.bookFormatId],
    references: [BookFormat.id],
  }),
  bookToBookThemes: many(BookToBookTheme),
  bookToContributors: many(BookToContributor),
  bookToKeywords: many(BookToKeyword),
  organization: one(organization, {
    fields: [Book.organizationId],
    references: [organization.id],
  }),
  royaltyToBooks: many(RoyaltyToBooks),
}))

export const InsertBookSchema = createInsertSchema(Book)
export const UpdateBookSchema = createUpdateSchema(Book)
  .omit({ id: true, legacyBookId: true, legacyNeonBookId: true })
  .partial()
export const SelectBookSchema = createSelectSchema(Book)

export type InsertBook = typeof Book.$inferInsert
export type UpdateBook = z.infer<typeof UpdateBookSchema>
export type SelectBook = typeof Book.$inferSelect
